import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {DownloadIcon, GraphIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, Button, FormControl, TextInput} from '@primer/react'
import {memo, useCallback, useEffect, useImperativeHandle, useState} from 'react'
import {flushSync} from 'react-dom'

import {ViewOptionsMenuUI} from '../../api/stats/contracts'
import {ViewTypeParam} from '../../api/view/contracts'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useEmojiAutocomplete} from '../../hooks/common/use-emoji-autocomplete'
import {useSyncedState} from '../../hooks/common/use-synced-state'
import {useDownloadView} from '../../hooks/use-download-view'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useIsCommandPaletteVisible} from '../../hooks/use-is-command-palette-visible'
import {useMemexProjectViewRootHeight} from '../../hooks/use-memex-app-root-height'
import type {BaseViewState, ViewIsDirtyStates} from '../../hooks/use-view-state-reducer/types'
import {useViews} from '../../hooks/use-views'
import {useInsightsEnabledFeatures} from '../../pages/insights/hooks/use-insights-features'
import {Link} from '../../router'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {PROJECT_INSIGHTS_ROUTE} from '../../routes'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {Resources} from '../../strings'
import {useSearch} from '../filter-bar/search-context'
import {ConfigurationItemMenus, ConfigurationItems} from './configuration-items'
import {LayoutToggle} from './layout-toggle'
import {DirtyConfigIcon, MenuAnchor} from './menu-anchor'
import {RoadmapUserSettingsMenu} from './roadmap-user-settings'
import {ViewActionItems} from './view-action-items'
import {ViewChangeButtons} from './view-changes-button'

type ViewOptionsMenuProps = {
  view: BaseViewState & ViewIsDirtyStates
  viewOptionMenuTriggerRef?: React.RefObject<HTMLButtonElement | null>
  showViewOptionsMenu?: () => void
}

const viewOptionsMenuOverlaySx = {overflow: 'auto'}
export const ViewOptionsMenu = memo(function ViewOptionsMenu({
  view,
  viewOptionMenuTriggerRef,
  showViewOptionsMenu,
}: ViewOptionsMenuProps) {
  const {memex_header_redesign} = useEnabledFeatures()
  const [open, setOpen] = useState(false)
  const [showSubMenu, setShowSubMenu] = useState<
    | null
    | 'Fields'
    | 'Sort by'
    | 'Group by'
    | 'Column by'
    | 'Field sum'
    | 'Dates'
    | 'Zoom level'
    | 'rename-view'
    | 'Marker fields'
    | 'Slice by'
  >(null)
  const {clientHeight} = useMemexProjectViewRootHeight({
    onResize: () => {
      if (open) {
        flushSync(() => {
          setOpen(false)
        })

        setOpen(true)
      }
    },
  })

  const {query} = useSearch()

  const {views, currentView, duplicateCurrentViewState, destroyCurrentView} = useViews()

  const {hasWritePermissions} = ViewerPrivileges()
  const {isInsightsChartViewEnabled} = useInsightsEnabledFeatures()
  const {anchorRef} = useViewOptionsMenuRef()

  useEffect(() => {
    if (!open) return
    setShowSubMenu(null)
  }, [open])

  const handleDestroyView = useCallback(async () => {
    destroyCurrentView(
      view.number,
      {
        ui: ViewOptionsMenuUI,
      },
      () => {
        setOpen(false)
        anchorRef.current?.focus()
      },
    )
  }, [destroyCurrentView, view.number, anchorRef])

  const handleDuplicateView = useCallback(async () => {
    if (!currentView) return
    await duplicateCurrentViewState(currentView.number, undefined, {
      ui: ViewOptionsMenuUI,
    })
    setOpen(false)
  }, [currentView, duplicateCurrentViewState])

  const handleRenameViewClick = useCallback(() => {
    setOpen(false)
    setShowSubMenu('rename-view')
  }, [])

  useImperativeHandle(viewOptionMenuTriggerRef, () => anchorRef.current)

  const {isCommandPaletteVisible} = useIsCommandPaletteVisible()

  const DirtyConfigIconForView = useCallback(() => {
    /** Only show dirty state for view type, other config buttons show dirty state in the toolbar */
    return <DirtyConfigIcon isViewStateDirty={view.isViewTypeDirty} />
  }, [view.isViewTypeDirty])

  const downloadView = useDownloadView()
  const projectRouteParams = useProjectRouteParams()
  return (
    <Box sx={{position: 'relative'}}>
      {memex_header_redesign ? null : (
        <MenuAnchor ref={anchorRef} onClick={() => setOpen(s => !s)} viewName={view.name} open={open} />
      )}
      <ActionMenu
        anchorRef={anchorRef}
        open={open && !isCommandPaletteVisible}
        onOpenChange={useCallback((nextOpen: boolean): void => setOpen(nextOpen), [])}
      >
        {memex_header_redesign ? (
          <ActionMenu.Button
            {...testIdProps('view-options-menu-toggle')}
            size="small"
            trailingAction={null}
            leadingVisual={DirtyConfigIconForView}
          >
            View options
          </ActionMenu.Button>
        ) : (
          <></>
        )}
        <ActionMenu.Overlay
          {...testIdProps('view-options-menu-modal')}
          width="medium"
          sx={viewOptionsMenuOverlaySx}
          style={{
            maxHeight: clientHeight,
          }}
        >
          <LayoutToggle view={view} setOpen={setOpen} />
          <ActionList sx={{pt: 0}}>
            <ActionList.Divider />
            <ConfigurationItems
              onSelect={option => {
                setShowSubMenu(option)
                setOpen(false)
              }}
            />
            {view.localViewState.layout === ViewTypeParam.Roadmap ? (
              <>
                <ActionList.Divider />
                <ActionList.Group title="User settings">
                  <RoadmapUserSettingsMenu />
                </ActionList.Group>
              </>
            ) : null}

            {isInsightsChartViewEnabled ? (
              <>
                <ActionList.Divider />
                <ActionList.Group>
                  <ActionList.LinkItem
                    as={Link}
                    {...testIdProps('view-options-menu-item-show-insights')}
                    to={{
                      pathname: PROJECT_INSIGHTS_ROUTE.generatePath(projectRouteParams),
                      search: query
                        ? `${new URLSearchParams({
                            filterQuery: query,
                          })}`
                        : undefined,
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <GraphIcon />
                    </ActionList.LeadingVisual>
                    Generate chart
                  </ActionList.LinkItem>
                </ActionList.Group>
              </>
            ) : null}

            {hasWritePermissions ? (
              <>
                <ActionList.Divider />
                <ViewActionItems
                  handleRenameViewClick={handleRenameViewClick}
                  handleDuplicateView={handleDuplicateView}
                  handleDestroyView={handleDestroyView}
                  viewsLength={views.length}
                  view={view}
                />
              </>
            ) : null}

            <ActionList.Divider />
            <ActionList.Item onClick={downloadView}>
              <ActionList.LeadingVisual>
                <DownloadIcon />
              </ActionList.LeadingVisual>
              Export view data
            </ActionList.Item>

            {!memex_header_redesign && view.isViewStateDirty ? (
              <>
                <ActionList.Divider />
                <ActionList.Group sx={{'& > ul': {display: 'flex'}}}>
                  <ViewChangeButtons setOpen={setOpen} view={view} />
                </ActionList.Group>
              </>
            ) : null}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <ConfigurationItemMenus
        anchorRef={anchorRef}
        currentMenu={showSubMenu === 'rename-view' ? null : showSubMenu}
        setShowSubMenu={setShowSubMenu}
      />
      {currentView ? (
        <RenameViewMenu
          key={view.id}
          anchorRef={anchorRef}
          open={showSubMenu === 'rename-view'}
          setOpen={(nextOpen: boolean) => setShowSubMenu(() => (nextOpen ? 'rename-view' : null))}
          view={currentView}
          showViewOptionsMenu={showViewOptionsMenu}
        />
      ) : null}
    </Box>
  )
})

const stopPropagation: React.MouseEventHandler<HTMLInputElement> = e => {
  e.stopPropagation()
}
/**
 * The control used in the roadmap view to specify the zoom level used in the view.
 */
const RenameViewMenu = memo<{
  open: boolean
  setOpen: (next: boolean) => void
  anchorRef: React.RefObject<HTMLElement>
  view: BaseViewState & ViewIsDirtyStates
  showViewOptionsMenu?: () => void
}>(function RenameViewMenu({open, setOpen, anchorRef, view, showViewOptionsMenu}) {
  const {renameView} = useViews()
  const [localViewName, setLocalViewName] = useSyncedState(view.name)
  const {hasWritePermissions} = ViewerPrivileges()

  const resetViewName = useCallback(() => {
    setLocalViewName(view.name)
  }, [setLocalViewName, view.name])

  const {clientHeight} = useMemexProjectViewRootHeight({
    onResize: () => {
      if (open) {
        flushSync(() => {
          setOpen(false)
        })
        setOpen(true)
      }
    },
  })
  const autocompleteProps = useEmojiAutocomplete()
  const onKeyDown = useCallback(
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (event.key) {
        case 'Enter': {
          if (localViewName.trim().length > 0 && localViewName !== view.name) {
            await renameView(view.number, localViewName, {
              ui: ViewOptionsMenuUI,
            })
            setOpen(false)
            showViewOptionsMenu?.()
          }

          event.preventDefault()
          break
        }

        case 'Escape': {
          resetViewName()
          break
        }
      }
    },
    [localViewName, view.name, view.number, renameView, setOpen, showViewOptionsMenu, resetViewName],
  )
  const inputCompositionProps = useIgnoreKeyboardActionsWhileComposing(onKeyDown)

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalViewName(e.target.value)
    },
    [setLocalViewName],
  )

  return (
    <ActionMenu open={open} anchorRef={anchorRef} onOpenChange={noop}>
      <ActionMenu.Overlay
        sx={{maxHeight: clientHeight, overflow: 'auto'}}
        {...testIdProps('rename-view-menu')}
        role="dialog"
        aria-label="Rename view"
        width="medium"
        onEscape={() => setOpen(false)}
        onClickOutside={() => setOpen(false)}
      >
        <Box>
          <Box sx={{p: 3, pb: 0, display: 'flex', flexDirection: 'column'}}>
            <FormControl>
              <FormControl.Label>Rename view</FormControl.Label>
              <InlineAutocomplete sx={{width: '100%'}} {...autocompleteProps}>
                <TextInput
                  block
                  autoComplete="off"
                  value={localViewName}
                  onChange={onChange}
                  onMouseDown={stopPropagation}
                  aria-label="Change view name"
                  disabled={!hasWritePermissions || view.isDeleted}
                  {...inputCompositionProps}
                  {...testIdProps(`view-name-input`)}
                />
              </InlineAutocomplete>
            </FormControl>
          </Box>
          <Box
            sx={{
              justifyContent: 'flex-end',
              px: 3,
              py: 2,
              mt: 4,
              borderTop: '1px solid',
              borderColor: 'border.muted',
              display: 'flex',
              gap: 1,
              backgroundColor: theme => `${theme.colors.canvas.default}`,
            }}
          >
            <Button
              onClick={e => {
                e.preventDefault()
                resetViewName()
                setOpen(false)
              }}
              size="small"
              type="reset"
              disabled={view.name === localViewName}
            >
              {Resources.discardChanges}
            </Button>
            <Button
              onClick={async e => {
                e.preventDefault()
                if (localViewName.trim().length === 0 || localViewName === view.name) return
                await renameView(view.number, localViewName, {
                  ui: ViewOptionsMenuUI,
                })
                setOpen(false)
                showViewOptionsMenu?.()
              }}
              size="small"
              type="submit"
              variant="primary"
              disabled={view.name === localViewName}
            >
              {Resources.saveChanges}
            </Button>
          </Box>
        </Box>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
})

export default ViewOptionsMenu

try{ ViewOptionsMenu.displayName ||= 'ViewOptionsMenu' } catch {}
try{ DirtyConfigIconForView.displayName ||= 'DirtyConfigIconForView' } catch {}
try{ RenameViewMenu.displayName ||= 'RenameViewMenu' } catch {}